import { AuthService, fmtMsg, GLUtil, locationReplace, LoginInfo } from "vue-glcommonui";
import { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import { store } from "@/store";
import { AppView } from "@/store/app/state";
import { LayoutGuard, ParentGuard, TeacherGuard } from ".";
import { Logger } from "@/utils/logger";
import { Button, notification } from "ant-design-vue";
import { h } from "vue";
import { ForceLogoutCodes, isIatSkewError } from "@/utils/utils";
import { MODULES, vuexName } from "@/store/utils";
import { AuthLocale } from "@/locales/localeid";

function formatDateEN(d: Date) {
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const HH = String(d.getHours()).padStart(2, "0");
  const MM = String(d.getMinutes()).padStart(2, "0");
  return sameYear ? `${HH}:${MM} (${dd}/${mm})` : `${HH}:${MM} (${dd}/${mm}/${yyyy})`;
}

function getClockSkewFromIdToken() {
  const { getTokenParam } = AuthService.getTokenUrlParams();
  const idt = getTokenParam("id_token") || {};

  const refSec = (typeof idt.iat === "number" && idt.iat) || (typeof idt.auth_time === "number" && idt.auth_time) || undefined;

  const nowMs = Date.now();
  const localDate = new Date(nowMs);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!refSec) {
    const mainMessage = fmtMsg(AuthLocale.ClockSkewNoData, {
      deviceTime: formatDateEN(localDate),
    });

    const devMeta = `[Metadata]
Timezone: ${tz}
Server now (ISO): N/A
Local now (ISO): ${new Date(nowMs).toISOString()}`;

    return { mainMessage, devMeta };
  }

  // Approximate server-now at issuance time from the token
  const serverNowApproxMs = refSec * 1000;
  const skewMs = nowMs - serverNowApproxMs;
  const skewMin = Math.round(skewMs / 60000);

  const absMin = Math.abs(skewMin);
  const absHour = Math.floor(absMin / 60);
  const remMin = absMin % 60;
  const dir =
    skewMin > 0 ? fmtMsg(AuthLocale.ClockSkewFaster) : skewMin < 0 ? fmtMsg(AuthLocale.ClockSkewSlower) : fmtMsg(AuthLocale.ClockSkewInSync);

  const diffText =
    absMin === 0
      ? ""
      : absHour > 0
      ? `${absHour} ${absHour > 1 ? fmtMsg(AuthLocale.ClockSkewHours) : fmtMsg(AuthLocale.ClockSkewHour)}${
          remMin ? " " + remMin + " " + (remMin > 1 ? fmtMsg(AuthLocale.ClockSkewMinutes) : fmtMsg(AuthLocale.ClockSkewMinute)) : ""
        }`
      : `${remMin} ${remMin > 1 ? fmtMsg(AuthLocale.ClockSkewMinutes) : fmtMsg(AuthLocale.ClockSkewMinute)}`;

  const mainMessage =
    absMin === 0
      ? fmtMsg(AuthLocale.ClockSkewCorrect, {
          deviceTime: formatDateEN(localDate),
        })
      : fmtMsg(AuthLocale.ClockSkewIncorrect, {
          deviceTime: formatDateEN(localDate),
          direction: dir,
          timeDiff: diffText,
        });

  const devMeta = `[Metadata]
Timezone: ${tz}
Server now (ISO): ${new Date(serverNowApproxMs).toISOString()}
Local now (ISO): ${new Date(nowMs).toISOString()}`;

  return { mainMessage, devMeta };
}

const notifySessionError = (code: ForceLogoutCodes, mainMessage: string, devMeta?: string) => {
  const key = `login-error-${Date.now()}`;
  if (code === ForceLogoutCodes.TokenExpired) {
    notification.error({
      message: fmtMsg(AuthLocale.LoginError),
      description: h("div", { style: { lineHeight: 1.5 } }, [
        h("div", { style: { marginBottom: "8px", fontSize: "14px" } }, mainMessage),
        devMeta
          ? h(
              "pre",
              {
                style: {
                  fontSize: "11px",
                  color: "#888",
                  background: "#f9f9f9",
                  padding: "6px",
                  whiteSpace: "pre-wrap",
                  maxHeight: "200px",
                  overflow: "auto",
                  margin: 0,
                },
              },
              devMeta,
            )
          : null,
      ]),
      duration: 0,
      btn: h(
        Button,
        {
          type: "primary",
          onClick: () => {
            AuthService.clearSignStorage();
            AuthService.storePagethenSignoutRedirect();
            notification.close(key);
          },
        },
        fmtMsg(AuthLocale.TryAgain),
      ),
      key,
    });
  } else {
    notification.error({
      message: fmtMsg(AuthLocale.LoginError),
      description: `Login process failed, please try again later (code: ${code}). ${mainMessage ? `: ${mainMessage}` : ""}`,
      duration: 100,
      btn: h(
        Button,
        {
          type: "primary",
          onClick: () => {
            AuthService.clearSignStorage();
            AuthService.storePagethenSignoutRedirect();
            notification.close(key);
          },
        },
        fmtMsg(AuthLocale.TryAgain),
      ),
      key,
    });
  }
};

const isTokenExpired = () => {
  const { getTokenParam } = AuthService.getTokenUrlParams();
  const exp = getTokenParam("id_token").exp;
  return !GLUtil.isNullOrUndefined(exp) && new Date(exp * 1000) < new Date();
};

const verifySession = () => {
  const abortSignin = () => {
    return new Promise<never>((_, reject) => {
      setTimeout(() => {
        locationReplace("/");
        reject();
      }, 10000);
    });
  };
  let expired: boolean;
  try {
    expired = isTokenExpired();
  } catch (e: any) {
    notifySessionError(ForceLogoutCodes.TokenExpired, `Error in token check: ${e.message || e}`);
    return abortSignin();
  }

  if (expired) {
    const { mainMessage, devMeta } = getClockSkewFromIdToken();
    notifySessionError(ForceLogoutCodes.TokenExpired, mainMessage, devMeta);

    return AuthService.clearState()
      .then(() => abortSignin())
      .catch(() => abortSignin());
  }

  try {
    AuthService.trySetSigninVerifyToken();
    return Promise.resolve();
  } catch (e: any) {
    notifySessionError(ForceLogoutCodes.TrySetSigninVerifyTokenError, `Session verification failed: ${e.message || e}`);
    return abortSignin();
  }
};

const routeAuth = (loginInfo: LoginInfo, to: RouteLocationNormalized, from: RouteLocationNormalized, next: any) => {
  const isNotAuthorized = !loginInfo.loggedin || GLUtil.isExpired(loginInfo);
  if (isNotAuthorized) {
    AuthService.storePagethenSigninRedirect();
  } else {
    if (to.meta) {
      if (store.getters.appView !== AppView.Authorized)
        store.dispatch(vuexName(MODULES.APP.DISPATCHES.SET_APP_VIEW, false), { appView: AppView.Authorized });
      if (store.getters[vuexName(MODULES.SPIN.GETTERS.GET_SPLASH)]) store.commit(vuexName(MODULES.SPIN.COMMITS.SET_SPLASH), false);

      const isTeacherGuardPassed = TeacherGuard(to, from, next);
      const isParentGuardPassed = ParentGuard(to, from, next);

      if (isTeacherGuardPassed && isParentGuardPassed) {
        if (to.name === "TeacherClass" || to.name === "StudentClass") {
          AuthService.signinSilent().then((loginInfo: any) => store.commit(vuexName(MODULES.AUTH.COMMITS.UPDATE_NEW_TOKEN), loginInfo));
        }
        LayoutGuard(to, from, next);
        next();
      } else {
        store.dispatch(vuexName(MODULES.APP.DISPATCHES.SET_APP_VIEW, false), { appView: AppView.UnAuthorized });
      }
    } else {
      store.dispatch(vuexName(MODULES.APP.DISPATCHES.SET_APP_VIEW, false), { appView: AppView.UnAuthorized });
    }
  }
};

export default (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const requiresAuth: boolean = to.matched.some((record) => record.meta.requiresAuth);
  const isSigninCallbackUrl = window.location.href.indexOf("#/oidc/signin#") !== -1;
  const isSilentCallbackUrl = window.location.href.indexOf("#/oidc/signin-silent#") !== -1;
  if (requiresAuth) {
    const isSigningOut = window.location.href.indexOf("oidc/signout") !== -1;
    if (isSigningOut) {
      const onSignedOut = () => {
        AuthService.clearSignStorage();
        const page = AuthService.getPageAfterSignout();
        locationReplace(page ? page.afterSignout || page.currentSignout : "/");
      };
      AuthService.signoutRedirectCallback()
        .then(onSignedOut)
        .catch((e: any) => {
          Logger.error(e);
        });
    } else if (isSigninCallbackUrl) {
      verifySession().then(() => {
        AuthService.signinRedirectCallback()
          .then((user: any) => {
            try {
              const page = AuthService.getPageAfterSignin();
              AuthService.setLoginInfo(true, user, () => {
                const replaceToSite = () => locationReplace(page ? page.afterSignin || page.shouldSignin : "/");
                try {
                  return replaceToSite();
                } catch (e) {
                  return replaceToSite();
                }
              });
            } catch (e) {
              locationReplace("/");
            }
          })
          .catch((e: any) => {
            if (isIatSkewError(e)) {
              const { mainMessage, devMeta } = getClockSkewFromIdToken();
              notifySessionError(ForceLogoutCodes.TokenExpired, mainMessage, devMeta);
              return;
            }

            notifySessionError(ForceLogoutCodes.ExchangeTokenAPIFailed, e?.message);
          });
      });
    } else if (isSilentCallbackUrl) {
      AuthService.signinSilentCallback().then((loginInfo: any) => AuthService.setLoginInfo(true, loginInfo));
    } else {
      const loginInfo = AuthService.getLoginInfo();
      if (loginInfo && loginInfo.loggedin) {
        routeAuth(loginInfo, to, from, next);
      } else {
        AuthService.useLocalStoreToLogin().then((loginInfo: any) => routeAuth(loginInfo, to, from, next));
      }
    }
  } else if (to.matched.some((record) => record.meta.notFound)) {
    store.dispatch(vuexName(MODULES.APP.DISPATCHES.SET_APP_VIEW, false), { appView: AppView.NotFound });
  } else {
    store.dispatch(vuexName(MODULES.APP.DISPATCHES.SET_APP_VIEW, false), { appView: AppView.Blank });
  }
};
